import { Alert } from "@/components/Alert";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type { ProviderConflict } from "@/pages/Providers/types";

interface ProviderConflictListProps {
    conflicts: ProviderConflict[];
    onView: (id: number) => void;
}

export const ProviderConflictList = ({
    conflicts,
    onView,
}: ProviderConflictListProps) => {
    const { t } = useProviderContext();
    if (conflicts.length === 0) {
        return (
            <Alert variant="success">{t("providers.ui.no_conflicts")}</Alert>
        );
    }
    return (
        <div className="flex flex-col gap-3">
            {conflicts.map((conflict, index) => (
                <Alert
                    key={`${conflict.fingerprint ?? conflict.field}-${index}`}
                    variant={
                        conflict.acknowledged
                            ? "info"
                            : conflict.severity === "blocking"
                              ? "error"
                              : "warning"
                    }
                >
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2 font-semibold">
                            {t(`providers.reasons.${conflict.reason}`)}
                            {conflict.field && (
                                <span>
                                    · {t(`providers.fields.${conflict.field}`)}
                                </span>
                            )}
                            <Badge
                                variant={
                                    conflict.severity === "blocking"
                                        ? "danger"
                                        : "warning"
                                }
                            >
                                {t(
                                    `providers.ui.${conflict.acknowledged ? "acknowledged" : conflict.severity}`
                                )}
                            </Badge>
                        </div>
                        {conflict.provider && (
                            <div className="flex flex-col gap-1 text-sm">
                                <span>
                                    {conflict.provider.name} ·{" "}
                                    {conflict.provider.tax_id}
                                </span>
                                <span>
                                    {conflict.provider.email} ·{" "}
                                    {conflict.provider.phone}
                                </span>
                                <span>
                                    {[
                                        conflict.provider.address,
                                        conflict.provider.address_extra,
                                        conflict.provider.postal_code,
                                        conflict.provider.city,
                                        conflict.provider.province,
                                    ]
                                        .filter(Boolean)
                                        .join(", ")}
                                </span>
                                <span>
                                    {t(
                                        `providers.filters.state_${Number(conflict.provider.is_active) === 1 ? "active" : "inactive"}`
                                    )}
                                </span>
                                <Button
                                    className="self-start"
                                    size="sm"
                                    variant="link"
                                    onClick={() =>
                                        onView(conflict.provider!.id)
                                    }
                                >
                                    {t("providers.ui.view")}
                                </Button>
                            </div>
                        )}
                    </div>
                </Alert>
            ))}
        </div>
    );
};
